import type { Seat } from '../../../shared/domain.ts';

export interface DiscProps {
  /** The seat occupying this cell, or 0 for empty. */
  readonly cell: number;
  /** The marker shape for this seat, from the spec's theme. */
  readonly marker?: 'filled' | 'ring';
  /**
   * `ghost` is "your piece would land here"; `suggested` is the review screen's
   * "the engine would have played here". The same device on purpose: the match
   * screen has already taught what a dashed outline on an empty cell means.
   */
  readonly variant?: 'placed' | 'ghost' | 'pending' | 'suggested';
  readonly justLanded?: boolean;
  readonly inWinningLine?: boolean;
}

/**
 * A single piece.
 *
 * Colour is never the only signal: every seat also carries a marker shape, so
 * the board stays readable in every common colour-vision deficiency and
 * survives a stream's video compression, where hues shift badly. Both the
 * colour and the marker come from the spec — `--seat-N` is set on the board's
 * root from `presentation.theme`, and this class names the shape.
 */
export function Disc({
  cell,
  marker = 'filled',
  variant = 'placed',
  justLanded,
  inWinningLine,
}: DiscProps) {
  if (cell === 0 && variant === 'placed') {
    return <span className="disc disc--empty" aria-hidden="true" />;
  }

  const seat: Seat = cell === 0 ? 1 : cell;
  const classes = [
    'disc',
    `disc--seat${seat}`,
    `disc--${marker}`,
    variant === 'ghost' && 'disc--ghost',
    variant === 'pending' && 'disc--pending',
    variant === 'suggested' && 'disc--suggested',
    justLanded && 'disc--landed',
    inWinningLine && 'disc--winner',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} aria-hidden="true">
      <span className="disc__marker" />
    </span>
  );
}
